import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABEL } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
  LayoutDashboard, Upload, Building2, Target, Layers, CalendarRange,
  BarChart3, ClipboardList, History, BellRing, FileDown, LogOut, Menu, X, KeyRound, UsersRound,
  Siren, FileText, ListChecks, Settings2, FileUp
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { SessionGuard } from "@/components/SessionGuard";

const NAV = [
  { heading: "Indikator Mutu", kepalaUnit: true },
  { label: "Dashboard Ringkasan", path: "/dashboard", icon: LayoutDashboard, testId: "nav-dashboard", kepalaUnit: true },
  { label: "Upload Data Bulanan", path: "/upload", icon: Upload, testId: "nav-upload" },
  { label: "Kelola Master Poliklinik", path: "/poliklinik", icon: Building2, testId: "nav-poliklinik" },
  { label: "Kelola Target Tahunan", path: "/target", icon: Target, testId: "nav-target" },
  { label: "Rekap per Departemen", path: "/rekap-departemen", icon: Layers, testId: "nav-rekap-departemen", kepalaUnit: true },
  { label: "Rekap per Bulan", path: "/rekap-bulan", icon: CalendarRange, testId: "nav-rekap-bulan", kepalaUnit: true },
  { label: "Dashboard Departemen", path: "/dashboard-departemen", icon: BarChart3, testId: "nav-dashboard-departemen", kepalaUnit: true },
  { label: "Ringkasan Keseluruhan", path: "/ringkasan", icon: ClipboardList, testId: "nav-ringkasan", kepalaUnit: true },
  { label: "Riwayat Upload", path: "/riwayat", icon: History, testId: "nav-riwayat" },
  { label: "Notifikasi Kelengkapan", path: "/notifikasi", icon: BellRing, testId: "nav-notifikasi" },
  { label: "Export Laporan", path: "/export", icon: FileDown, testId: "nav-export" },
  { heading: "Laporan SIPORT", siportViewer: true, kepalaUnit: true },
  { label: "Dashboard SIPORT", path: "/siport/dashboard", icon: Siren, testId: "nav-siport-dashboard", siportViewer: true, kepalaUnit: true },
  { label: "Data Laporan SIPORT", path: "/siport/data", icon: FileText, testId: "nav-siport-data", siportViewer: true, kepalaUnit: true },
  { label: "Master Opsi SIPORT", path: "/siport/master", icon: ListChecks, testId: "nav-siport-master" },
  { label: "Pengaturan Form Publik", path: "/siport/pengaturan", icon: Settings2, testId: "nav-siport-pengaturan" },
  { label: "Import Data Lama", path: "/siport/import", icon: FileUp, testId: "nav-siport-import" },
  { heading: "Akun" },
  { label: "Kelola Akun User", path: "/users", icon: UsersRound, testId: "nav-users", superOnly: true },
  { label: "Ganti Password", path: "/ganti-password", icon: KeyRound, testId: "nav-ganti-password" },
];

export function Layout({ children }) {
  const { user, logout, isSiportViewer, isKepalaUnit } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50">
      <SessionGuard />
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-50 h-screen w-72 shrink-0 bg-brand-teal text-white flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-3 px-6 h-16 border-b border-white/15">
          <img src="/logo-simutu.png" alt="SIMUTU" className="w-9 h-9 rounded-lg shrink-0 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-white text-base">SIMUTU</div>
            <div className="text-[11px] text-white/95">Rawat Jalan RS</div>
          </div>
          <button className="lg:hidden ml-auto text-white" onClick={() => setOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-3 space-y-1">
          {NAV.filter((item) => (isKepalaUnit ? item.kepalaUnit : isSiportViewer ? item.siportViewer : !item.superOnly || user?.role === "super_admin")).map((item) => {
            if (item.heading) {
              return (
                <div key={item.heading} className="px-3 pt-4 pb-1 text-[11px] font-bold uppercase tracking-wider text-white/70 first:pt-0">
                  {item.heading}
                </div>
              );
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={item.testId}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
                    isActive
                      ? "bg-brand-sky text-brand-deep shadow-sm"
                      : "text-white/90 hover:bg-white/10 hover:text-white"
                  }`
                }
              >
                <Icon className="w-[18px] h-[18px] shrink-0" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-white/15 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-brand-lime text-brand-deep flex items-center justify-center font-bold text-sm">
              {(user?.nama || "A").charAt(0)}
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-sm font-semibold text-white truncate">{user?.nama || "Admin"}</div>
              <div className="text-[11px] text-white/95 truncate">
                @{user?.username}
                <span className="ml-1.5 text-white">· {ROLE_LABEL[user?.role] || "Admin"}</span>
              </div>
            </div>
          </div>
          {isSiportViewer && (
            <NavLink
              to="/ganti-password"
              data-testid="nav-profile-ganti-password"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-sm font-medium transition-colors duration-150 ${
                  isActive ? "bg-brand-sky text-brand-deep" : "text-white/90 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <KeyRound className="w-4 h-4 shrink-0" /> Ganti Password
            </NavLink>
          )}
          <Button
            variant="outline"
            size="sm"
            data-testid="nav-logout-button"
            onClick={() => logout()}
            className="w-full bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" /> Keluar
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 h-16 flex items-center gap-3 px-4 sm:px-6 border-b border-border bg-white/90 backdrop-blur-md lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="menu-toggle">
            <Menu className="w-6 h-6 text-slate-700" />
          </button>
          <img src="/logo-simutu.png" alt="" className="w-7 h-7 rounded-md object-contain" />
          <span className="font-display font-bold text-slate-900">SIMUTU Rawat Jalan</span>
        </header>
        <motion.main
          key={window.location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
}
